// Slice 45 — Consent tab. Rolls up the consent-ledger across active
// customers and shows opt-in count + percentage per frozen purpose.

import type { ConsentRollup } from "../../api/client";
import { HqCard, Icons } from "../../ui";

const PURPOSE_LABEL: Record<string, string> = {
  telemetry: "Telemetry",
  marketing: "Marketing",
  product_news: "Product news",
};

const PURPOSE_DESCRIPTION: Record<string, string> = {
  telemetry: "Anonymised counters: instance count, feature usage, error keys.",
  marketing: "Product / company announcements and promotional email.",
  product_news: "Release notes + behavioural changes (recommended for paid customers).",
};

export interface ConsentTabProps {
  rollup: ConsentRollup | null;
}

export function ConsentTab({ rollup }: ConsentTabProps) {
  if (!rollup) {
    return (
      <HqCard title="Consent" icon={Icons.Shield}>
        <p
          style={{
            margin: 0,
            color: "var(--hq-text-dim)",
            fontSize: 13,
            textAlign: "center",
            padding: "16px 0",
          }}
        >
          Loading…
        </p>
      </HqCard>
    );
  }
  if (rollup.totalCustomers === 0) {
    return (
      <HqCard title="Consent" icon={Icons.Shield}>
        <p
          style={{
            margin: 0,
            color: "var(--hq-text-dim)",
            fontSize: 13,
            textAlign: "center",
            padding: "16px 0",
          }}
        >
          No active customers yet. Per-customer consent is captured on Customer detail (Slice 43).
        </p>
      </HqCard>
    );
  }
  return (
    <HqCard
      title="Consent rollup"
      icon={Icons.Shield}
      right={
        <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
          {rollup.totalCustomers} active customers
        </span>
      }
    >
      <p
        style={{
          margin: "0 0 14px",
          fontSize: 12,
          color: "var(--hq-text-muted)",
          lineHeight: 1.55,
        }}
      >
        Latest-per-purpose across the append-only ledger. Capture mechanics: in-app modal, email
        link, or operator-recorded; the source is part of every ledger row.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rollup.byPurpose.map((p) => (
          <PurposeRow
            key={p.purpose}
            purpose={p.purpose}
            optInCount={p.optInCount}
            percentage={p.percentage}
            total={rollup.totalCustomers}
          />
        ))}
      </div>
    </HqCard>
  );
}

function PurposeRow({
  purpose,
  optInCount,
  percentage,
  total,
}: {
  purpose: string;
  optInCount: number;
  percentage: number;
  total: number;
}) {
  const label = PURPOSE_LABEL[purpose] ?? purpose;
  const description = PURPOSE_DESCRIPTION[purpose] ?? "";
  return (
    <div
      style={{
        padding: 12,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--hq-text)", flex: 1 }}>
          {label}
        </span>
        <span
          className="hq-mono"
          style={{ fontSize: 13, fontWeight: 600, color: "var(--hq-accent)" }}
        >
          {percentage}%
        </span>
        <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
          {optInCount}/{total}
        </span>
      </div>
      <div style={{ fontSize: 11, color: "var(--hq-text-dim)", marginBottom: 8, lineHeight: 1.45 }}>
        {description}
      </div>
      <div
        style={{
          height: 6,
          background: "var(--hq-bg-active)",
          borderRadius: 99,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(2, percentage)}%`,
            height: "100%",
            background: "linear-gradient(90deg, var(--hq-accent-deep), var(--hq-accent))",
            boxShadow: "0 0 4px rgba(34,211,238,.55)",
          }}
        />
      </div>
    </div>
  );
}
